import React from 'react';
import { useIntl } from 'react-intl';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';

interface Props {
  brandName: string;
  market: string | null;
  models: string[];
  statementCount: number;
  competitorCount: number;
  // Statement replicates per model per pass, from the read's sampling.
  perStatement: number;
  rank: {
    question: string;
    perModelSearched: number;
    perModelMemory: number;
    totalAnswers: number;
  } | null;
  hasSurvey: boolean;
  // v4: the searched view came from reading the pages, not from asking again.
  pagesMode?: boolean;
}

const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const Step: React.FunctionComponent<{ n: number; title: string; first?: boolean; children: React.ReactNode }> = ({
  n,
  title,
  first,
  children,
}) => (
  <div
    style={{
      padding: '4px 22px 4px',
      borderLeft: first ? 'none' : `1px solid ${THEME.borderSubtle}`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 7,
          background: THEME.textBase,
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: 14.5, fontWeight: 700, color: THEME.textBase }}>{title}</span>
    </div>
    <div style={{ fontSize: 13.5, lineHeight: 1.55, color: THEME.textSecondary }}>{children}</div>
  </div>
);

// The reader is a marketer who has never seen the method. Before any number,
// say in three steps what was done: a shopper's question, the ratings, and
// where the customer line comes from. Every count is the run's own.
export const HowWeAsked: React.FunctionComponent<Props> = ({
  brandName,
  market,
  models,
  statementCount,
  competitorCount,
  perStatement,
  rank,
  hasSurvey,
  pagesMode = false,
}) => {
  const intl = useIntl();
  const who = models.length ? joinNames(models) : 'ChatGPT, Claude and Gemini';
  const where = market ? ` in ${market}` : '';

  return (
    <Card>
      <Eyebrow style={{ marginBottom: 10 }}>How we got these numbers</Eyebrow>
      <p style={{ fontSize: 14.5, lineHeight: 1.6, color: THEME.textBase, margin: '0 0 18px', maxWidth: '80ch' }}>
        When a shopper asks an AI assistant what to buy, the answer comes from what that assistant
        believes about the brands. We asked the models behind {who} directly, as a shopper{where},
        and recorded what they said. Nothing here was written by us.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', margin: '0 -22px' }}>
        <Step n={1} title="Would they recommend you?" first>
          Each model was asked{rank ? ` “${rank.question}” ` : ' which brands it would consider, in order, '}
          {rank
            ? `${rank.perModelSearched} times with live web search and ${rank.perModelMemory} times from memory alone, ${rank.totalAnswers} answers in all.`
            : 'many times, with and without web search.'}{' '}
          {brandName} was never mentioned in the question, so every mention is the model's own.
        </Step>
        <Step n={2} title="What do they believe about you?">
          Each model read {statementCount} statements about {brandName}, the same statements customers
          answer in a Brand Equity survey, and said how much it agrees with each from 1 (strongly
          disagree) to 7 (strongly agree).{' '}
          {pagesMode
            ? intl.formatMessage(
                {
                  id: 'marketerView.modelBeliefs.howWeAsked.pages',
                  defaultMessage:
                    'Every statement was asked {n} times per model, from what it already knew. Then every page the models read in at least two of their searched answers was fetched and read once, with search off, against the same statements: a score from that page alone, with the page’s own words.',
                },
                { n: perStatement },
              )
            : `Every statement was asked ${perStatement} times per model, once from what it already knew and once again after searching the web.`}
          {competitorCount > 0
            ? ` The same was done for ${competitorCount} competitor${competitorCount === 1 ? '' : 's'}.`
            : ''}
        </Step>
        <Step n={3} title="Are they right?">
          {hasSurvey
            ? 'Your customers have answered the same statements in your survey, so their answer sits beside the models’ wherever both exist. Where they differ, the models are wrong about you, or the web is telling them something your customers do not experience.'
            : 'Only your customers can say. The survey built from these same statements is ready to send; once it has answers, the customer line appears beside the models’ line.'}
        </Step>
      </div>
      <div style={{ fontSize: 12, color: THEME.textMuted, marginTop: 16 }}>
        Scores are averages across every answer. A model that said it held no view on a statement is
        reported as that, not averaged in.
      </div>
    </Card>
  );
};
